import { useRequest, useApplicantAPI } from "@certn/api";
import {
  ActionList,
  ActionListItem,
  Container,
  Divider,
  Loader,
  Text,
} from "@certn/cascadia-react";
import { Toggle } from "@certn/cascadia-react/components/inputs/toggle/Toggle";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { AppTitle } from "../../components/AppTitle";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";

type NotificationOptions = "expiring-soon" | "marketing-and-promotions";

const urlOptionToDataAttribute = {
  "expiring-soon": "has_case_expiration_notifications_on",
  "marketing-and-promotions": "has_consented_to_marketing",
};

export function NotificationsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("Notifications — Certn"));
  const [updating, setUpdating] = useState<string>();
  const request = useRequest();
  const { data, isLoading, mutate } = useApplicantAPI(
    "/api/internal/applicant/me/notifications/"
  );

  if (!data) {
    if (isLoading) {
      return <Loader overlay />;
    }
  }

  const updateNotificationSettings = async (
    notificationType: NotificationOptions,
    enabled: boolean
  ) => {
    const path = `/api/internal/applicant/me/notifications/${notificationType}/update/`;
    const requestData = {
      method: "POST",
      body: JSON.stringify({
        [urlOptionToDataAttribute[notificationType]]: enabled,
      }),
    };

    try {
      setUpdating(notificationType);
      await request(path, requestData);
      mutate(
        data
          ? {
              ...data,
              [urlOptionToDataAttribute[notificationType]]: !enabled,
            }
          : undefined
      );
    } finally {
      setUpdating(undefined);
    }
  };

  return (
    <Container>
      <AppTitle title="Notifications" />
      <ActionList ariaLabel={t("Notifications")}>
        <>
          <ActionListItem
            displayAccessibility
            title={t("Expiring soon")}
            trailing={
              <Toggle
                testID="expiring-soon-notification"
                id="toggle"
                aria-label="toggle"
                isSelected={data?.has_case_expiration_notifications_on}
                onChange={(value) =>
                  updateNotificationSettings("expiring-soon", value)
                }
                isDisabled={updating === "expiring-soon"}
              />
            }
          >
            <Text style="global/body-secondary" color="text-weak">
              <Trans>Email me when an application will expire soon</Trans>
            </Text>
          </ActionListItem>
          <Divider />
          <ActionListItem
            displayAccessibility
            title={t("Certn updates and promotions")}
            trailing={
              <Toggle
                testID="updates-and-promotions-notification"
                id="updates-and-promotions"
                aria-label="toggle"
                isSelected={data?.has_consented_to_marketing}
                onChange={(value) =>
                  updateNotificationSettings("marketing-and-promotions", value)
                }
                isDisabled={updating === "marketing-and-promotions"}
              />
            }
          >
            <Text style="global/body-secondary" color="text-weak">
              <Trans>
                Email me about news, feature updates, discounts and offers from
                Certn
              </Trans>
            </Text>
          </ActionListItem>
        </>
      </ActionList>
    </Container>
  );
}
